import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import { useContext, useEffect, useMemo, useState } from 'react';
import {
  COMPARE_BY_ENUM,
  COMPARE_BY_OPTIONS,
  DEFAULT_COMPARE_DATE_OPTIONS,
  DEFAULT_DATE_OPTIONS
} from '../components/content/analytics/analyticsConfig';
import { getFromStorage, saveToStorage } from './storageHelper';
import { getDateOnly } from './format';
import { Context as RegistrationAdminContext } from '../providers/RegistrationAdminProvider';
import { REPORT_FIELD_LOGIC_OPERATORS } from '../components/content/analytics/reportsConfig';

const getDatePeriodNumber = (str) => {
  const match = str.match(/^-?\d+/);
  return match ? Number(match[0]) : 0;
};

const periodHandlers = {
  d: (date, num) => date.setDate(date.getDate() + num),
  w: (date, num) => date.setDate(date.getDate() + num * 7),
  m: (date, num) => date.setMonth(date.getMonth() + num),
  y: (date, num) => date.setFullYear(date.getFullYear() + num),
  q: (date, num) => date.setMonth(date.getMonth() + num * 3)
};

const getDateParamValue = (param) => {
  const lowerParam = param.toLowerCase();
  const period = lowerParam.slice(-1); // Get the last character
  const num = getDatePeriodNumber(param);

  const baseDate = new Date();
  if (periodHandlers[period]) {
    periodHandlers[period](baseDate, num);
    return baseDate;
  }
  return dayjs(param);
};

const getDefaultPeriodInterval = (startDate, endDate) => {
  const daysBetween = dayjs(endDate).diff(dayjs(startDate), 'day');

  if (daysBetween <= 2) {
    return 'hour';
  }
  if (daysBetween <= 30) {
    return 'day';
  }
  return 'month';
};

const areDatesEqual = (date1, date2) => {
  const normalizeDate = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  return normalizeDate(new Date(date1)) === normalizeDate(new Date(date2));
};

const processProductCounts = (
  analytics,
  dataLabel,
  compareDataLabel,
  dataKey,
  nameKey
) => {
  let chartData = analytics.data
    .map((d) => d[dataKey])
    .flat()
    .reduce((r, c) => {
      const existingProduct = r.find(
        (d) => d.vendorProductUuid === c.vendorProductUuid
      );
      if (existingProduct) {
        existingProduct[dataLabel] += c.count;
      }
      else {
        r.push({
          vendorProductUuid: c.vendorProductUuid,
          name: c[nameKey],
          [dataLabel]: c.count
        });
      }
      return r;
    }, []);

  if (analytics.compareData) {
    chartData = analytics.compareData
      .map((d) => d[dataKey])
      .flat()
      .reduce((r, c) => {
        const existingProduct = r.find(
          (d) => d.vendorProductUuid === c.vendorProductUuid
        );
        if (existingProduct) {
          existingProduct[compareDataLabel] =
            (existingProduct[compareDataLabel] ?? 0) + c.count;
        }
        else {
          r.push({
            vendorProductUuid: c.vendorProductUuid,
            name: c[nameKey],
            [compareDataLabel]: c.count
          });
        }
        return r;
      }, chartData);
  }

  return chartData
    .sort(
      (a, b) =>
        b[dataLabel] - a[dataLabel] ||
        b[compareDataLabel] - a[compareDataLabel] ||
        a.name.localeCompare(b.name)
    )
    .map((m) => ({
      ...m,
      [dataLabel]: m[dataLabel] || 0,
      [compareDataLabel]: m[compareDataLabel] || 0
    }));
};

const parseReportFiltersFromURL = (params, defaultFilters = []) => {
  const raw = params.get('filters');
  if (!raw) return defaultFilters;

  return raw.split(',').map((f) => {
    const parts = f.split(':');
    const field = parts[0];
    const compare = parts[1];
    const values = [];
    const operators = [];

    // Parse values and operators
    for (let i = 2; i < parts.length; i++) {
      const part = parts[i];
      const partLower = part?.toLowerCase();
      if (partLower === 'and' || partLower === 'or') {
        operators.push(partLower);
      }
      else {
        values.push(part);
      }
    }

    return {
      field,
      compare,
      values,
      logicalOperator:
        operators[0] ||
        REPORT_FIELD_LOGIC_OPERATORS.filter(
          (op) => !op.blockedBounds || !op.blockedBounds.includes(compare)
        )[0].value
    };
  });
};

const serializeReportFiltersToURL = (filters) => {
  if (!filters || !filters.length) return '';

  const serialized = filters.map((f) => {
    const { field, compare, values, logicalOperator } = f;

    if (!field || !compare || !values?.length) {
      return '';
    }

    const parts = [field, compare];

    if (values.length > 1) {
      for (let i = 0; i < values.length; i++) {
        parts.push(values[i]);
        if (i < values.length - 1) {
          parts.push(logicalOperator.toLowerCase());
        }
      }
    }
    else {
      parts.push(values[0]);
    }

    return parts.join(':');
  });

  return serialized.filter(Boolean).join(',');
};

const parametersEnum = {
  start: 'start',
  end: 'end',
  dateCompare: 'dateCompare',
  compareStart: 'compareStart',
  compareEnd: 'compareEnd',
  period: 'period',
  season: 'season',
  compareSeason: 'compareSeason',
  division: 'division',
  compareDivision: 'compareDivision',
  compare: 'compare',
  view: 'view',
  viewType: 'viewType',
  noDateRange: 'noDateRange',
  formUuid: 'formUuid'
};

const useAnalyticsFilter = (
  resourceEntityId,
  { defaultFilters, defaultDateOption, includePeriod, includeCompareDateRange },
  onFetchAnalytics
) => {
  const { state } = useContext(RegistrationAdminContext);
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const [hasChangedFilters, setHasChangedFilters] = useState(false);

  // extract parameters
  const searchParams = new URLSearchParams(search);
  const startParam = searchParams.get(parametersEnum.start);
  const endParam = searchParams.get(parametersEnum.end);
  const dateCompareParam = searchParams.get(parametersEnum.dateCompare);
  const compareStartParam = searchParams.get(parametersEnum.compareStart);
  const compareEndParam = searchParams.get(parametersEnum.compareEnd);
  const periodParam = searchParams.get(parametersEnum.period);

  const seasonParam = searchParams.get(parametersEnum.season);
  const compareSeasonParam = searchParams.get(parametersEnum.compareSeason);

  const divisionParam = searchParams.get(parametersEnum.division);
  const compareDivisionParam = searchParams.get(parametersEnum.compareDivision);

  const formUuidParam = searchParams.get(parametersEnum.formUuid);

  const compareParam = searchParams.get(parametersEnum.compare);
  const viewParam = searchParams.get(parametersEnum.view);
  const viewTypeParam = searchParams.get(parametersEnum.viewType);
  const noDateRange =
    searchParams.get(parametersEnum.noDateRange)?.toLocaleLowerCase() === 'yes';

  const filters = parseReportFiltersFromURL(
    searchParams,
    hasChangedFilters ? null : defaultFilters
  );

  // determine primary date period
  const datePeriod =
    startParam && endParam
      ? DEFAULT_DATE_OPTIONS.find(
          (f) => f.start === startParam && f.end === endParam
        )
      : DEFAULT_DATE_OPTIONS.find((f) => f.value === defaultDateOption) ||
        DEFAULT_DATE_OPTIONS.find((f) => f.isDefault);
  const { startDate, endDate } = datePeriod
    ? datePeriod.getDateRange()
    : {
        startDate: getDateParamValue(startParam),
        endDate: getDateParamValue(endParam)
      };

  // determine comparison date period
  const compareDatePeriod =
    compareStartParam && compareEndParam
      ? DEFAULT_COMPARE_DATE_OPTIONS.find(
          (f) => f.start === compareStartParam && f.end === compareEndParam
        )
      : DEFAULT_COMPARE_DATE_OPTIONS.find(
          (f) => f.value === dateCompareParam
        ) || DEFAULT_COMPARE_DATE_OPTIONS.find((f) => f.isDefault);
  const {
    startDate: compareStartDate,
    endDate: compareEndDate
  } = compareDatePeriod
    ? compareDatePeriod.getDateRange(datePeriod, startDate, endDate)
    : {
        startDate: getDateParamValue(compareStartParam),
        endDate: getDateParamValue(compareEndParam)
      };

  // setup season/division options
  const seasonOptions = useMemo(
    () =>
      (state.regAssociation.value?.regAssociationSeasons || [])
        .map((s) => ({
          label: s.name,
          value: s.pkRegAssociationSeason.toString(),
          endDate: s.endDate
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [state]
  );

  const divisionOptions = useMemo(
    () =>
      (state.regAssociation.value?.regAssociationDivisions || [])
        .map((d) => ({
          label: d.name,
          value: d.pkRegAssociationDivision.toString(),
          endDate: d.regAssociationSeason.endDate,
          seasonName: d.regAssociationSeason.name,
          seasonValue: d.regAssociationSeason.pkRegAssociationSeason.toString()
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [state]
  );

  const formOptions = useMemo(
    () =>
      (state.regAssociation.value?.regAssociationDivisionForms || [])
        .filter((f) => f.regFormSubmissionCount > 0)
        .reduce((r, c) => {
          const existingRegForm = r.find((f) => f.value === c.regForm.uuid);
          if (
            existingRegForm &&
            !existingRegForm.divisions.find(
              (d) =>
                d.fkRegAssociationDivision ===
                c.regAssociationDivision.pkRegAssociationDivision
            )
          ) {
            existingRegForm.divisions.push({
              fkRegAssociationDivision: c.regAssociationDivision.pkRegAssociationDivision.toString(),
              fkRegAssociationSeason: c.regAssociationDivision.fkRegAssociationSeason.toString()
            });
          }
          else {
            r.push({
              value: c.regForm.uuid,
              label: c.regForm.name,
              divisions: [
                {
                  fkRegAssociationDivision: c.regAssociationDivision.pkRegAssociationDivision.toString(),
                  fkRegAssociationSeason: c.regAssociationDivision.fkRegAssociationSeason.toString()
                }
              ]
            });
          }

          return r;
        }, [])
        .sort((a, b) => a.label.localeCompare(b.label)),
    [state]
  );

  const sortedSeasonsByDate = useMemo(
    () =>
      [...seasonOptions].sort(
        (a, b) => new Date(b.endDate) - new Date(a.endDate)
      ),
    [seasonOptions]
  );

  const sortedDivisionsByDate = useMemo(
    () =>
      [...divisionOptions].sort(
        (a, b) => new Date(b.endDate) - new Date(a.endDate)
      ),
    [divisionOptions]
  );

  const defaultSeasonOption = sortedSeasonsByDate[0]?.value;
  const defaultCompareSeasonOption =
    sortedSeasonsByDate.filter((s) => new Date(s.endDate) < new Date())[0]
      ?.value || sortedSeasonsByDate[0]?.value;

  const defaultDivisionOption = sortedDivisionsByDate[0]?.value;
  const defaultCompareDivisionOption =
    sortedDivisionsByDate.filter((d) => new Date(d.endDate) < new Date())[0]
      ?.value || sortedDivisionsByDate[0]?.value;

  const selectedOptions =
    compareParam === COMPARE_BY_ENUM.SEASON
      ? [
          {
            valueName: 'season',
            value:
              (seasonParam &&
                seasonOptions.find((d) => d.value.toString() === seasonParam)
                  ?.value) ||
              defaultSeasonOption
          },
          {
            valueName: 'compareSeason',
            value:
              (compareSeasonParam &&
                seasonOptions.find(
                  (d) => d.value.toString() === compareSeasonParam
                )?.value) ||
              defaultCompareSeasonOption
          }
        ]
      : compareParam === COMPARE_BY_ENUM.DIVISION
      ? [
          {
            valueName: 'division',
            value:
              (divisionParam &&
                divisionOptions.find(
                  (d) => d.value.toString() === divisionParam
                )?.value) ||
              defaultDivisionOption
          },
          {
            valueName: 'compareDivision',
            value:
              (compareDivisionParam &&
                divisionOptions.find(
                  (d) => d.value.toString() === compareDivisionParam
                )?.value) ||
              defaultCompareDivisionOption
          }
        ]
      : [
          {
            valueName: 'season',
            value:
              seasonParam &&
              seasonOptions.find((d) => d.value.toString() === seasonParam)
                ?.value
          },
          {
            valueName: 'division',
            value:
              divisionParam &&
              divisionOptions.find((d) => d.value.toString() === divisionParam)
                ?.value
          },
          {
            valueName: 'formUuid',
            value:
              formUuidParam &&
              formOptions.find((d) => d.value === formUuidParam)?.value
          }
        ];

  // build filter object
  const filter = {
    datePeriod,
    compareDatePeriod,
    noDateRange,
    startDate: startDate && !noDateRange ? getDateOnly(startDate) : null,
    endDate: endDate && !noDateRange ? getDateOnly(endDate) : null,
    compareStartDate:
      compareStartDate && !noDateRange ? getDateOnly(compareStartDate) : null,
    compareEndDate:
      compareEndDate && !noDateRange ? getDateOnly(compareEndDate) : null,
    periodInterval:
      periodParam?.toLowerCase() ??
      getDefaultPeriodInterval(startDate, endDate),
    view: viewParam,
    viewType: viewTypeParam,
    compareBy:
      COMPARE_BY_OPTIONS.find((op) => op.value === compareParam)?.value ||
      COMPARE_BY_OPTIONS.find((op) => op.isDefault)?.value ||
      COMPARE_BY_OPTIONS[0].value,
    dateCompareBy: dateCompareParam,
    options: selectedOptions,
    filters
  };

  useEffect(() => {
    if (
      resourceEntityId &&
      state.regAssociation.value &&
      (filter.compareBy === COMPARE_BY_ENUM.PURCHASE_DATE ||
        filter.options?.every((op) => !!op.value))
    ) {
      onFetchAnalytics(filter);
    }
  }, [resourceEntityId, state.regAssociation.value]);

  const getDateCompareParams = (newSearchParams, newFilter) => {
    if (newFilter.noDateRange) {
      newSearchParams.delete('start');
      newSearchParams.delete('end');
      newSearchParams.delete('compare');
      newSearchParams.delete('compareStart');
      newSearchParams.delete('compareEnd');
      newSearchParams.set('noDateRange', 'yes');
    }
    else {
      newSearchParams.delete('noDateRange');
      newSearchParams.set(
        parametersEnum.start,
        newFilter.datePeriod
          ? newFilter.datePeriod.start
          : dayjs(newFilter.startDate).format('YYYY-MM-DD')
      );
      newSearchParams.set(
        parametersEnum.end,
        newFilter.datePeriod
          ? newFilter.datePeriod.end
          : dayjs(newFilter.endDate).format('YYYY-MM-DD')
      );

      if (includeCompareDateRange) {
        if (newFilter.compareDatePeriod) {
          newSearchParams.set('dateCompare', newFilter.compareDatePeriod.value);
        }
        else {
          newSearchParams.set(
            parametersEnum.compareStart,
            dayjs(newFilter.compareStartDate).format('YYYY-MM-DD')
          );
          newSearchParams.set(
            parametersEnum.compareEnd,
            dayjs(newFilter.compareEndDate).format('YYYY-MM-DD')
          );
        }
      }

      if (includePeriod) {
        newSearchParams.set(parametersEnum.period, newFilter.periodInterval);
      }
    }

    const seasonOption = newFilter.options.find(
      (o) => o.valueName === 'season'
    );
    if (seasonOption && seasonOption.value) {
      newSearchParams.set(parametersEnum.season, seasonOption.value);
    }

    const divisionOption = newFilter.options.find(
      (o) => o.valueName === 'division'
    );
    if (divisionOption && divisionOption.value) {
      newSearchParams.set(parametersEnum.division, divisionOption.value);
    }

    const formOption = newFilter.options.find(
      (o) => o.valueName === 'formUuid'
    );
    if (formOption && formOption.value) {
      newSearchParams.set(parametersEnum.formUuid, formOption.value);
    }
  };

  const getSeasonCompareParams = (newSearchParams, newFilter) => {
    const seasonOption = newFilter.options.find(
      (o) => o.valueName === 'season'
    );
    if (seasonOption) {
      newSearchParams.set(parametersEnum.season, seasonOption.value);
    }

    const compareOption = newFilter.options.find(
      (o) => o.valueName === 'compareSeason'
    );
    if (compareOption) {
      newSearchParams.set(parametersEnum.compareSeason, compareOption.value);
    }
  };

  const getDivisionCompareParams = (newSearchParams, newFilter) => {
    const divisionOption = newFilter.options.find(
      (o) => o.valueName === 'division'
    );
    if (divisionOption) {
      newSearchParams.set(parametersEnum.division, divisionOption.value);
    }

    const compareOption = newFilter.options.find(
      (o) => o.valueName === 'compareDivision'
    );
    if (compareOption) {
      newSearchParams.set(parametersEnum.compareDivision, compareOption.value);
    }
  };

  const getSearchParams = (newFilter) => {
    const newSearchParams = new URLSearchParams(search);

    // remove old params
    Object.values(parametersEnum).forEach((value) => {
      newSearchParams.delete(value);
    });

    // set view params
    newSearchParams.set(parametersEnum.compare, newFilter.compareBy);
    if (newFilter.view) {
      newSearchParams.set(parametersEnum.view, newFilter.view);
      if (newFilter.viewType) {
        newSearchParams.set(parametersEnum.viewType, newFilter.viewType);
      }
    }

    // add params based on compareBy
    switch (newFilter.compareBy) {
      case COMPARE_BY_ENUM.SEASON:
        getSeasonCompareParams(newSearchParams, newFilter);
        break;
      case COMPARE_BY_ENUM.DIVISION:
        getDivisionCompareParams(newSearchParams, newFilter);
        break;
      case COMPARE_BY_ENUM.PURCHASE_DATE:
      default:
        getDateCompareParams(newSearchParams, newFilter);
        break;
    }

    newSearchParams.delete('filters');
    if (newFilter.filters && newFilter.filters.length) {
      newSearchParams.set(
        'filters',
        serializeReportFiltersToURL(newFilter.filters)
      );
    }

    return newSearchParams;
  };

  const initializeFilter = (newFilter) => {
    switch (newFilter.compareBy) {
      case COMPARE_BY_ENUM.SEASON:
        return {
          ...newFilter,
          options: [
            {
              valueName: 'season',
              value:
                newFilter.options?.find((o) => o.valueName === 'season')
                  ?.value || defaultSeasonOption
            },
            {
              valueName: 'compareSeason',
              value:
                newFilter.options?.find((o) => o.valueName === 'compareSeason')
                  ?.value ||
                defaultCompareSeasonOption ||
                defaultSeasonOption
            }
          ]
        };
      case COMPARE_BY_ENUM.DIVISION:
        return {
          ...newFilter,
          options: [
            {
              valueName: 'division',
              value:
                newFilter.options?.find((o) => o.valueName === 'division')
                  ?.value || defaultDivisionOption
            },
            {
              valueName: 'compareDivision',
              value:
                newFilter.options?.find(
                  (o) => o.valueName === 'compareDivision'
                )?.value ||
                defaultCompareDivisionOption ||
                defaultDivisionOption
            }
          ]
        };
      case COMPARE_BY_ENUM.PURCHASE_DATE:
      default:
        return { ...newFilter };
    }
  };

  const onUpdateFilter = (newFilter) => {
    setHasChangedFilters(true);

    let updatedFilter = { ...newFilter };
    if (updatedFilter.compareBy !== filter.compareBy) {
      updatedFilter = initializeFilter(updatedFilter);
    }
    onFetchAnalytics(updatedFilter);

    const newSearchParams = getSearchParams(updatedFilter);
    navigate(`${pathname}?${newSearchParams.toString()}`, { replace: true });
  };

  return {
    filter,
    optionsData: {
      seasons: seasonOptions,
      divisions: divisionOptions,
      forms: formOptions
    },
    onChange: onUpdateFilter,
    urlSearchParams: getSearchParams(filter)
  };
};

const useReportHistory = () => {
  const historyString = getFromStorage('reportHistory');
  const reportHistory = historyString
    ? JSON.parse(historyString)
    : {
        reports: [],
        params: {}
      };

  const onUpdateReportUuidHistory = (reportUuid) => {
    const newReportHistory = {
      ...reportHistory,
      reports: [
        {
          uuid: reportUuid,
          date: new Date()
        },
        ...reportHistory.reports.filter((f) => f.uuid !== reportUuid)
      ]
    };
    saveToStorage('reportHistory', JSON.stringify(newReportHistory));
  };

  return {
    reportHistory,
    onViewReport: onUpdateReportUuidHistory
  };
};

export {
  processProductCounts,
  useAnalyticsFilter,
  areDatesEqual,
  getDefaultPeriodInterval,
  useReportHistory,
  parseReportFiltersFromURL
};
